// Custom lightweight-charts series that fills the area between the prediction
// high/low lines. Used by MultiPaneChart via chart.addCustomSeries().

class PredictionBandRenderer {
  constructor() {
    this._data = null;
    this._options = null;
  }

  update(data, options) {
    this._data = data;
    this._options = options;
  }

  draw(target, priceConverter) {
    const data = this._data;
    if (!data || !data.visibleRange || !data.bars.length) return;

    const { bars, visibleRange } = data;
    const from = Math.max(Math.floor(visibleRange.from), 0);
    const to = Math.min(Math.ceil(visibleRange.to), bars.length);
    if (to - from < 2) return;

    const fillColor = this._options?.fillColor || 'rgba(29, 78, 216, 0.18)';

    // `bar.x` and `priceConverter` return media coordinates.
    // Draw in media space to keep the band aligned when browser zoom/devicePixelRatio changes.
    target.useMediaCoordinateSpace(({ context }) => {
      context.save();
      context.beginPath();
      context.fillStyle = fillColor;

      let started = false;
      for (let i = from; i < to; i += 1) {
        const bar = bars[i];
        const high = bar.originalData?.high;
        if (!Number.isFinite(high)) continue;
        const y = priceConverter(high);
        if (y === null) continue;
        if (!started) {
          context.moveTo(bar.x, y);
          started = true;
        } else {
          context.lineTo(bar.x, y);
        }
      }

      for (let i = to - 1; i >= from; i -= 1) {
        const bar = bars[i];
        const low = bar.originalData?.low;
        if (!Number.isFinite(low)) continue;
        const y = priceConverter(low);
        if (y === null) continue;
        context.lineTo(bar.x, y);
      }

      if (started) {
        context.closePath();
        context.fill();
      }
      context.restore();
    });
  }
}

class PredictionBandSeries {
  constructor() {
    this._renderer = new PredictionBandRenderer();
  }

  renderer() {
    return this._renderer;
  }

  update(data, options) {
    this._renderer.update(data, options);
  }

  priceValueBuilder(plotRow) {
    const high = plotRow?.high;
    const low = plotRow?.low;
    if (!Number.isFinite(high) || !Number.isFinite(low)) return [];
    return [high, low, (high + low) / 2];
  }

  isWhitespace(data) {
    return !data || !Number.isFinite(data.high) || !Number.isFinite(data.low);
  }

  defaultOptions() {
    return {
      color: 'rgba(0, 0, 0, 0)',
      fillColor: 'rgba(29, 78, 216, 0.18)',
    };
  }
}

export default PredictionBandSeries;
